import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { Info, RotateCcw } from 'lucide-react';
import { useUnitOptions } from '@/components/internal/line-items/hooks/useUnitOptions';
import { unitOptions as defaultUnitOptions } from '@/types/unit-types';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface LineItemData {
  description: string;
  base_cost: number;
  base_unit: string;
  markup_percentage: number;
  unit_cost: number;
  source_type: 'internal' | 'supplier' | 'custom';
  source_item_id?: string;
}

export interface EditLineItemUpdates {
  description: string;
  base_cost: number;
  base_unit: string;
  markup_percentage: number;
}

interface EditLineItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  lineItem: LineItemData | null;
  onSave: (updates: EditLineItemUpdates) => void;
}

const EditLineItemDialog: React.FC<EditLineItemDialogProps> = ({
  isOpen,
  onClose,
  lineItem,
  onSave,
}) => {
  const { profile } = useAuth();
  const { customUnitOptions } = useUnitOptions(profile?.company_id);
  const [markup, setMarkup] = useState<number | ''>(0);
  const [description, setDescription] = useState('');
  const [baseCost, setBaseCost] = useState<number | ''>(0);
  const [baseUnit, setBaseUnit] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (lineItem) {
      setMarkup(lineItem.markup_percentage);
      setDescription(lineItem.description);
      setBaseCost(lineItem.base_cost);
      setBaseUnit(lineItem.base_unit);
    }
  }, [lineItem]);

  if (!lineItem) return null;

  const effectiveBaseCost = baseCost === '' ? 0 : baseCost;
  const effectiveMarkup = markup === '' ? 0 : markup;
  const previewUnitCost = effectiveBaseCost * (1 + effectiveMarkup / 100);
  const isCatalogItem = lineItem.source_type !== 'custom';

  // Merge default and custom units, deduplicating by value
  const allUnits = [...defaultUnitOptions];
  for (const cu of customUnitOptions) {
    if (!allUnits.find(u => u.value === cu.value)) {
      allUnits.push(cu);
    }
  }

  const handleResetToDefaults = async () => {
    if (!lineItem.source_item_id || !isCatalogItem) return;

    setResetting(true);
    try {
      // Both internal and supplier items now live in internal_line_items
      const table = 'internal_line_items';
      const { data, error } = await supabase
        .from(table)
        .select('description, base_cost, base_unit, markup_percentage, final_price')
        .eq('id', lineItem.source_item_id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Source item not found');

      setDescription((data as any).description);
      setBaseCost((data as any).base_cost);
      setBaseUnit((data as any).base_unit);
      setMarkup((data as any).markup_percentage ?? 0);
      toast.success('Reset to catalog defaults');
    } catch (error) {
      console.error('Failed to fetch catalog defaults:', error);
      toast.error('Could not fetch original catalog values');
    } finally {
      setResetting(false);
    }
  };

  const handleSave = () => {
    onSave({
      description: description.trim() || lineItem.description,
      base_cost: baseCost === '' ? 0 : baseCost,
      base_unit: baseUnit,
      markup_percentage: markup === '' ? 0 : markup,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Line Item</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="base_cost">Base Cost</Label>
              <Input
                id="base_cost"
                type="number"
                value={baseCost}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') setBaseCost('');
                  else { const num = parseFloat(val); if (!isNaN(num)) setBaseCost(num); }
                }}
                onBlur={() => { if (baseCost === '') setBaseCost(0); }}
                step="0.01"
                min="0"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="base_unit">Unit</Label>
              <Select value={baseUnit} onValueChange={setBaseUnit}>
                <SelectTrigger id="base_unit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {allUnits.map((u) => (
                    <SelectItem key={u.value} value={u.value}>
                      {u.label} ({u.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="markup">Markup Percentage (%)</Label>
            <Input
              id="markup"
              type="number"
              value={markup}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') setMarkup('');
                else { const num = parseFloat(val); if (!isNaN(num)) setMarkup(num); }
              }}
              onBlur={() => { if (markup === '') setMarkup(0); }}
              step="0.5"
              min="0"
              className="font-mono"
            />
          </div>

          <div className="flex justify-between text-sm border-t pt-3">
            <span className="text-muted-foreground">Unit Price (preview)</span>
            <span className="font-mono font-medium">{formatCurrency(previewUnitCost)}</span>
          </div>

          {isCatalogItem && (
            <>
              <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>Changes apply to this invoice only and won't affect the source catalog item.</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleResetToDefaults}
                disabled={resetting}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-2" />
                {resetting ? 'Resetting...' : 'Reset to Catalog Defaults'}
              </Button>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditLineItemDialog;
